import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";
import { toast } from "sonner";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";

export interface Dentista {
  id: string;
  user_id?: string;
  nome: string;
  cro: string;
  especialidade: string;
  telefone: string;
  email: string;
  cpf: string;
  endereco?: string;
  cep?: string;
  data_nascimento?: string;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  status: "Ativo" | "Inativo";
  created_at: string;
  updated_at: string;
}

export interface CreateDentistaData {
  nome: string;
  cro: string;
  especialidade: string;
  telefone: string;
  email: string;
  cpf: string;
  endereco?: string;
  cep?: string;
  data_nascimento?: string;
  recebe_meta?: boolean;
  meta_periodo?: string;
  meta_inicio?: string;
  meta_fim?: string;
  meta_valor?: number;
  meta_bonus?: number;
  recebe_comissao?: boolean;
  percentual_comissao?: number;
  status: "Ativo" | "Inativo";
}

export function useDentistas() {
  const [dentistas, setDentistas] = useState<Dentista[]>([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const activeClinicaId = useActiveClinicaId();

  const fetchDentistas = async () => {
    if (!user) {
      setDentistas([]);
      return;
    }
    try {
      setLoading(true);
      const data = await apiList<Dentista>("dentistas");
      setDentistas(data.filter((d) => d.status !== "Inativo").sort((a, b) => a.nome.localeCompare(b.nome)));
    } catch (error: any) {
      toast.error("Erro ao carregar dentistas: " + error.message);
      setDentistas([]);
    } finally {
      setLoading(false);
    }
  };

  const createDentista = async (dentistaData: CreateDentistaData) => {
    if (!user) return null;
    try {
      const data = await apiCreate<Dentista>("dentistas", dentistaData);
      await fetchDentistas();
      toast.success("Dentista cadastrado com sucesso!");
      return data;
    } catch (error: any) {
      toast.error("Erro ao cadastrar dentista: " + error.message);
      return null;
    }
  };

  const updateDentista = async (id: string, dentistaData: Partial<CreateDentistaData>) => {
    try {
      await apiUpdate<Dentista>("dentistas", id, dentistaData);
      await fetchDentistas();
      toast.success("Dentista atualizado com sucesso!");
      return true;
    } catch (error: any) {
      toast.error("Erro ao atualizar dentista: " + error.message);
      return false;
    }
  };

  const deleteDentista = async (id: string) => {
    try {
      await apiDelete("dentistas", id);
      await fetchDentistas();
      toast.success("Dentista removido com sucesso!");
      return true;
    } catch (error: any) {
      toast.error("Erro ao remover dentista: " + error.message);
      return false;
    }
  };

  useEffect(() => {
    fetchDentistas();
  }, [user?.id, activeClinicaId]);

  return { dentistas, loading, createDentista, updateDentista, deleteDentista, refetch: fetchDentistas };
}
